import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { CircularProgress, Grid, Box, Typography, Alert, AlertTitle, Button as MuiButton, Breadcrumbs, Link } from '@mui/material';
import { NavigateNext as NavigateNextIcon, Home as HomeIcon, Assessment as AssessmentIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import ModuleActionCard from '../components/common/ModuleActionCard';
import Module1Service, { 
  useStartAnalysisDb, 
  useGetAnalysisStatusDb
} from '../services/module1Service';
import { useDirectModule1ToModule2 } from '../services/module2Service';
import { useAppContext, AppProvider } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { useDatabaseMode } from '../context/DatabaseModeContext';
import { transformAnalysisResults } from '../utils/analysis';
import { useWorkflow, useCreateWorkflow } from '../hooks/useWorkflow';
import { useWorkflowContext } from '../context/WorkflowContext';

import { ErrorAlert } from '../components/module1/ErrorAlert';
import WorkflowStepper from '@/components/WorkflowStepper';
import { getWorkflowStatus } from '@/utils/moduleStatus';

// Import the new components
import Module2Integration, { Module2IntegrationRef } from '../components/module1/Module2Integration';
import AnalysisResultsComponent from '../components/module1/AnalysisResults';
import WorkflowCreationForm from '../components/module1/WorkflowCreationForm';
import AnalysisInputs from '../components/module1/AnalysisInputs';
import SkipToModule3 from '../components/SkipToModule3';


// Import the handlers from the new location
import { useHandlers } from '../components/module1/hooks';

// Wrapper component to ensure AppProvider context is available
const Module1AnalysisWrapper: React.FC = () => {
  // Check if we're already inside an AppProvider context
  try {
    useAppContext(); // This will throw if we're not in an AppProvider
    // We're already in an AppProvider, so render directly
    return <Module1Analysis />;
  } catch (error) {
    // We're not in an AppProvider, so wrap with it
    return (
      <AppProvider>
        <Module1Analysis />
      </AppProvider>
    );
  }
};

export const Module1Analysis: React.FC = () => {
  const navigate = useNavigate();
  const { serviceStatus, updateWorkflowData, workflowData } = useAppContext();
  const { showToast } = useToast();
  
  // Get workflow data from context
  const { 
    workflowId, 
    clientId, 
    clientName, 
    workflowName,
    workflow,
    triggerRefresh 
  } = useWorkflowContext();
  
  const createWorkflowMutation = useCreateWorkflow();
  
  // Direct Module 1 to Module 2 integration using v4 endpoint
  const directModule1ToModule2Mutation = useDirectModule1ToModule2();
  const [isClassifying, setIsClassifying] = useState(false);
  const [classificationResult, setClassificationResult] = useState<any>(null);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  
  // Database-first approach hooks
  const [activeModuleId, setActiveModuleId] = useState<number | null>(null);
  const [shouldPoll, setShouldPoll] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Only poll when we have an active module and should poll (simplified)
  const analysisStatusDbQuery = useGetAnalysisStatusDb(
    shouldPoll && activeModuleId ? activeModuleId : 0
  );
  
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [serviceHealth, setServiceHealth] = useState<{ status: string; message?: string; checks?: any } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFilePath, setUploadedFilePath] = useState<string | null>(null);
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  const [isCreatingWorkflow, setIsCreatingWorkflow] = useState(false);
  const [businessName, setBusinessName] = useState<string>('');
  const { isUsingDatabaseApproach, setIsUsingDatabaseApproach } = useDatabaseMode();
  const [isStepperCollapsed, setIsStepperCollapsed] = useState(false);
  const [hideResults, setHideResults] = useState(true); // Always start with input form

  // Memoize the handlers props to prevent infinite loops
  const handlersProps = useMemo(() => ({
    workflowId,
    clientId,
    workflow,
    workflowData,
    uploadedFilePath,
    setUploadedFilePath,
    setHasUploadedFile,
    isUsingDatabaseApproach,
    setIsUsingDatabaseApproach,
    isProcessing,
    setIsProcessing,
    setError,
    setIsUploading,
    setIsCreatingWorkflow,
    setBusinessName,
    setAnalysisResults,
    setActiveModuleId,
    activeModuleId,
    setIsStepperCollapsed,
    businessName,
    triggerRefresh
  }), [
    workflowId,
    clientId,
    workflow,
    workflowData,
    uploadedFilePath,
    isUsingDatabaseApproach,
    isProcessing,
    activeModuleId,
    businessName,
    triggerRefresh
  ]);

  // Use our custom hook for all handler functions
  const {
    handleCreateWorkflow,
    handleFileUpload,
    handleDbAnalysisComplete,
    handleReprocess,
    handleDatabaseToggleChange,
    handleStepperCollapseChange,
    cleanupPolling
  } = useHandlers(handlersProps);

  // Add this at the component level, before useEffects
  const processedResultsRef = useRef(false);

  // Create a ref to access the child component methods
  const module2IntegrationRef = useRef<Module2IntegrationRef>(null);

  // Add these handlers for the ModuleActionCard
  const handleExport = useCallback(() => {
    // Check if we have analysis results before attempting to export
    if (!analysisResults) {
      showToast('No analysis results available to send to Module 2', 'error');
      return;
    }

    // Use the ref to call the method directly on the child component
    if (module2IntegrationRef.current) {
      module2IntegrationRef.current.handleExport();
    } else {
      console.error("Module2Integration component not ready");
      showToast('Module 2 integration is not ready yet. Please try again.', 'error');
    }
  }, [analysisResults, showToast]);

  const handleViewExportResults = useCallback(() => {
    // Check if we have classification results before attempting to view them
    if (classificationResult) {
      setIsResultModalOpen(true);
    } else {
      showToast('No export results available yet. Please export to Module 2 first.', 'warning');
    }
  }, [classificationResult, showToast]);

  const handleGoToModule2 = useCallback(() => {
    navigate(`/module2/client/${clientId}/${workflowId}`);
  }, [navigate, clientId, workflowId]);

  // Add handler for loading latest results
  const handleLoadLatestResults = useCallback((results: any) => {
    if (results) {
      console.log('Loading latest results:', results);
      setAnalysisResults(results);
      
      // Update workflow data to maintain consistency
      updateWorkflowData({
        ...workflowData,
        analysisResult: results,
      });
      
      showToast('Latest results loaded successfully', 'success');
    }
  }, [updateWorkflowData, workflowData, showToast]);

  // Handler for the Load Latest Results button - simply show existing results
  const handleShowResults = useCallback(() => {
    setHideResults(false);
    showToast('Showing existing results', 'info');
  }, [showToast]);

  // Check service health on component mount and initialize database-first approach
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const health = await Module1Service.getInstance().getHealth();
        setServiceHealth(health);
        
        if (health.status !== 'healthy') {
          if (health.checks && health.checks.blob_storage === 'error') {
            setError('Module 1 service has storage connectivity issues. Please try again later or contact support.');
          } else {
            console.warn('Module 1 service reports unhealthy status but storage is connected. Proceeding with reduced functionality.');
          }
        }
      } catch (err) {
        console.error('Failed to check service health:', err);
        setError('Unable to connect to Module 1 service. Please check if the service is running.');
      }
    };
    
    // If we're using database-first and have a workflow ID, try to get the module ID
    if (isUsingDatabaseApproach && workflowId) {
      // Try to get the database workflow ID
      const dbWorkflowId = localStorage.getItem('db_workflow_id');
      if (dbWorkflowId) {
        // We have a database workflow ID, check for activeModuleId in localStorage
        const storedModuleId = localStorage.getItem(`workflow_${dbWorkflowId}_module_id`);
        if (storedModuleId) {
          setActiveModuleId(parseInt(storedModuleId, 10));
        }
      }
    }
    
    checkHealth();
  }, [workflowId]);

  // Check for selected client ID and redirect if not present
  useEffect(() => {
    // Only check if we're not in a workflow already and don't have a client ID in the URL
    if (!workflowId && !clientId) {
      const selectedClientId = localStorage.getItem('selected_client_id');
      if (selectedClientId) {
        // If we have a client ID in localStorage but not in URL, redirect to include it
        navigate(`/module1/client/${selectedClientId}`);
      } else {
        // Redirect to client selection page if no client ID anywhere
        navigate('/client-selection');
        showToast('Please select a client first', 'info');
      }
    }
  }, [workflowId, clientId, navigate, showToast]);

  // Reset component state when navigating to a different workflow
  useEffect(() => {
    // Reset all analysis-related state when workflow changes
    setAnalysisResults(null);
    setBusinessName('');
    setUploadedFilePath(null);
    setHasUploadedFile(false);
    setError(null);
    setHideResults(true);
    setClassificationResult(null);
    setIsResultModalOpen(false);
    processedResultsRef.current = false;
    
    console.log(`Workflow changed to ${workflowId}, resetting component state`);
  }, [workflowId, clientId]);



  // Client data is now provided by WorkflowContext
  
  // Process database-first analysis status updates (but don't auto-load results)
  useEffect(() => {
    // Only proceed if we're using database approach and have analysis status data
    if (isUsingDatabaseApproach && analysisStatusDbQuery.data && activeModuleId) {
      console.log('Database analysis status update:', analysisStatusDbQuery.data);
      
      // Handle completed analysis - auto-load results and switch to results view
      if (analysisStatusDbQuery.data.results && !processedResultsRef.current) {
        console.log('Results detected in status update, loading and switching to results view');
        
        // Mark that we've processed these results
        processedResultsRef.current = true;
        
        // Extract and set the results
        const rawResults = analysisStatusDbQuery.data.results;
        const minimallyProcessedResults = {
          topics: rawResults.topics || [],
          missing_themes: rawResults.missing_themes || []
        };
        
        setAnalysisResults(minimallyProcessedResults);
        setHideResults(false); // Switch to results view
        
        updateWorkflowData({
          ...workflowData,
          analysisResult: minimallyProcessedResults,
        });
        
        showToast('Analysis completed successfully!', 'success');
        setIsProcessing(false);
        setShouldPoll(false); // Stop polling when results are available
      }
      // Handle status checks
      else if (['completed', 'draft', 'finalized'].includes(analysisStatusDbQuery.data.status)) {
        console.log('Completed status detected, results available for manual loading');
        showToast('Analysis completed! Results are ready to load.', 'success');
        setIsProcessing(false);
        setShouldPoll(false); // Stop polling when completed
      } 
      // Handle failed analysis
      else if (analysisStatusDbQuery.data.status === 'failed') {
        const errorMessage = analysisStatusDbQuery.data.error || 'Analysis failed';
        setError(errorMessage);
        showToast(errorMessage, 'error');
        setIsProcessing(false);
        setShouldPoll(false); // Stop polling when failed
      }
      // For processing status, we let the React Query hook continue polling
      else if (analysisStatusDbQuery.data.status === 'processing') {
        // Keep the loading state active and continue polling
        setIsProcessing(true);
        setShouldPoll(true); // Ensure polling continues during processing
      }
    }
  }, [isUsingDatabaseApproach, analysisStatusDbQuery.data, activeModuleId, showToast, updateWorkflowData, workflowData]);

  // Add an effect to reset the processed flag when active module changes
  useEffect(() => {
    // Reset the processed flag and restart polling when module ID changes
    processedResultsRef.current = false;
    if (activeModuleId) {
      setShouldPoll(true); // Resume polling for new analysis
    }
  }, [activeModuleId]);

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      setShouldPoll(false); // Stop polling on unmount
      cleanupPolling();
    };
  }, [cleanupPolling]);

  // Only show workflow creation UI if no workflowId is provided
  if (!workflowId) {
    return (
      <WorkflowCreationForm
        onCreateWorkflow={handleCreateWorkflow}
        isCreating={isCreatingWorkflow}
      />
    );
  }


  return (
    <Box className="container mx-auto py-6">


          {/* Error alerts are always visible */}
          <ErrorAlert error={error} serviceHealth={serviceHealth} />



          {/* Update grid container to match other modules */}
          <Grid container spacing={3} columnGap={!isStepperCollapsed ? 3 : 0}>
            {/* WorkflowStepper is always visible */}
            <Grid item xs="auto">
              <WorkflowStepper
                activeStep={0}            
                workflowId={workflowId || ''}
                clientId={clientId}
                clientName={clientName}
                moduleCompletionStatus={getWorkflowStatus(workflowId)}
                onCollapseChange={handleStepperCollapseChange}
              />
            </Grid>
            
            {/* Main content area with loading overlay */}
            <Grid 
              item 
              xs
              sx={{
                pl: isStepperCollapsed ? 1 : 3,  // More left padding when stepper is open
                position: 'relative' // Add relative positioning for loading overlay
              }}
            >
 
              {/* Working breadcrumb navigation with back button */}
              {clientId && workflowId && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Breadcrumbs 
                    aria-label="breadcrumb" 
                    sx={{ '& .MuiBreadcrumbs-separator': { color: 'text.secondary' } }}
                  >
                    <Link 
                      color="inherit" 
                      href="/" 
                      onClick={(e) => { e.preventDefault(); navigate('/'); }}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                      Dashboard
                    </Link>
                    <Typography color="text.secondary">
                      {clientName || `Client ${clientId}`} ({clientId} - {workflowId})
                    </Typography>
                    <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                      <AssessmentIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                      Module 1: Keyword Analysis
                    </Typography>
                  </Breadcrumbs>
                  
                  <MuiButton
                    variant="outlined"
                    size="small"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/start-process')}
                    sx={{ ml: 2 }}
                  >
                    Start Process
                  </MuiButton>
                </Box>
              )}

              {/* Main content - always fully interactive */}
              <Box>

                {!analysisResults || hideResults ? (
                  <AnalysisInputs
                    onAnalysisComplete={handleDbAnalysisComplete}
                    onFileUpload={handleFileUpload}
                    isProcessing={isProcessing}
                    isUploading={isUploading}
                    uploadedFilePath={uploadedFilePath}
                    hasUploadedFile={hasUploadedFile}
                    isUsingDatabaseApproach={isUsingDatabaseApproach}
                    onResultsLoaded={handleLoadLatestResults}
                    onShowResults={handleShowResults}
                    storedKeywordCount={analysisResults?.keyword_count}
                    totalKeywordCount={analysisResults?.keyword_count}
                    clientId={clientId}
                    clientName={clientName}
                    workflowId={workflowId}
                  />
                ) : (
                  <Box>
                    <AnalysisResultsComponent
                      data={analysisResults}
                      workflowId={workflowId!}
                      isProcessing={isProcessing}
                      serviceAvailable={!!serviceStatus?.module1}
                      onReprocess={handleReprocess}
                      onResultsLoaded={(results) => {
                        if (results) {
                          const transformedData = transformAnalysisResults(results);
                          setAnalysisResults(transformedData);
                          updateWorkflowData({
                            ...workflowData,
                            analysisResult: transformedData,
                          });
                        }
                      }}
                      onEditInputs={() => setHideResults(!hideResults)}
                    />                 
                    
                    
                    {/* Pass ref to Module2Integration */}
                    <Module2Integration
                      ref={module2IntegrationRef}
                      workflowId={workflowId}
                      clientId={clientId}
                      analysisResults={analysisResults}
                      uploadedFilePath={uploadedFilePath}
                      isServiceAvailable={!!serviceStatus?.module2}
                      setIsClassifying={setIsClassifying}
                      setClassificationResult={setClassificationResult}
                      isClassifying={isClassifying}
                      classificationResult={classificationResult}
                      isResultModalOpen={isResultModalOpen}
                      setIsResultModalOpen={setIsResultModalOpen}
                    />
                  </Box>
                )}
                {/* Module 2 and Module 3 actions - only show when not editing inputs */}
                {!hideResults && (
                  <>
                    {/* Skip to Module 3 Option - positioned next to Module 2 actions */}
                    {workflowId && clientId && analysisResults && (
                      <Box sx={{ mb: 2 }}>
                        <SkipToModule3
                          workflowId={workflowId}
                          clientId={clientId || ''}
                        />
                      </Box>
                    )}
                    
                    <ModuleActionCard
                      nextModuleNumber={2}
                      title="Send Analysis to Module 2"
                      isExporting={isClassifying}
                      exportSuccess={!!classificationResult}
                      onExport={handleExport}
                      onViewExportResults={handleViewExportResults}
                      onGoToNextModule={handleGoToModule2}
                      exportButtonText="Send to Module 2"
                      nextModuleButtonText="Go to Module 2"
                      viewResultsButtonText="View Exported Results"
                      disabled={!analysisResults || isClassifying}
                    />
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
    </Box>
  );
};

// Export the wrapper instead of the component directly
export default Module1AnalysisWrapper;
